import { Instagram, Twitter, Globe } from 'lucide-react';
import manarLogo from '@/assets/manar-creative-studio-logo.png';

const Footer = () => {
  return (
    <footer
      dir="ltr"
      className="relative overflow-hidden bg-gradient-to-b from-[#070f1f] via-[#08122a] to-[#050a18] pt-4 pb-4 px-4 -mt-2"
    >
      {/* Top hairline */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[hsl(42_55%_70%/0.35)] to-transparent"
      />

      {/* Soft cinematic gold glow — quieter */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[320px] h-[320px] rounded-full
                     bg-[radial-gradient(circle,hsl(42_70%_58%/0.08),transparent_72%)] blur-3xl"
        />
        {/* Vignette */}
        <div
          className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_55%,rgba(0,0,0,0.55)_100%)]"
        />
      </div>

      {/* Subtle particles */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        {[...Array(5)].map((_, i) => (
          <span
            key={i}
            className="mcs-particle absolute block rounded-full bg-[hsl(42_70%_72%)]"
            style={{
              left: `${(i * 19 + 8) % 100}%`,
              top: `${(i * 31 + 20) % 100}%`,
              width: '2px',
              height: '2px',
              animationDelay: `${i * 1.2}s`,
              animationDuration: `${9 + (i % 4)}s`,
              opacity: 0.2,
              boxShadow: '0 0 4px hsl(42 75% 65% / 0.35)',
            }}
          />
        ))}
      </div>

      <div className="relative max-w-md mx-auto flex flex-col items-center mcs-fade">
        {/* Brand logo — refined glow */}
        <a
          href="https://show-your-shine-06.lovable.app/manar-creative-studio"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Manar Creative Studio"
          className="relative block group -mt-1"
        >
          <div
            aria-hidden
            className="absolute inset-0 -m-5 rounded-full bg-[radial-gradient(circle,hsl(42_70%_60%/0.14),transparent_72%)] blur-2xl mcs-pulse"
          />
          <img
            src={manarLogo}
            alt="Manar Creative Studio"
            width={520}
            height={520}
            loading="lazy"
            className="relative w-36 sm:w-44 h-auto drop-shadow-[0_0_10px_hsl(42_75%_60%/0.16)]
                       transition-transform duration-700 group-hover:scale-[1.02]"
          />
        </a>

        {/* Divider — unified rhythm: 12px (mt-3 mb-3) between blocks */}
        <div
          aria-hidden
          className="mt-3 mb-3 w-24 h-px bg-gradient-to-r from-transparent via-[hsl(42_55%_70%/0.35)] to-transparent"
        />

        {/* Kifah institutional signature */}
        <p
          className="text-[hsl(42_55%_82%/0.85)] text-[9px] font-light uppercase mb-3"
          style={{ letterSpacing: '0.38em' }}
        >
          Kifah Academy · Al-Ahsa
        </p>

        <div className="flex items-center gap-5 mb-3">
          <a
            href="https://instagram.com/kifahacademy"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Kifah Academy on Instagram"
            className="text-[hsl(42_75%_78%)] hover:text-[hsl(42_85%_88%)] transition-colors duration-500"
          >
            <Instagram strokeWidth={1.7} className="w-[15px] h-[15px]" />
          </a>
          <a
            href="https://x.com/KifahAcademy"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Kifah Academy on X"
            className="text-[hsl(42_75%_78%)] hover:text-[hsl(42_85%_88%)] transition-colors duration-500"
          >
            <Twitter strokeWidth={1.7} className="w-[15px] h-[15px]" />
          </a>
          <a
            href="https://alkifah.sch.sa"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Kifah Academy official website"
            className="text-[hsl(42_75%_78%)] hover:text-[hsl(42_85%_88%)] transition-colors duration-500"
          >
            <Globe strokeWidth={1.7} className="w-[15px] h-[15px]" />
          </a>
        </div>

        {/* Copyright */}
        <p className="text-white/60 text-[9px] tracking-[0.16em] text-center font-light">
          © 2026 Manar Creative Studio — All Rights Reserved
        </p>
      </div>

      <style>{`
        .mcs-fade { animation: mcs-fade 1.2s ease-out both; }
        @keyframes mcs-fade {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .mcs-pulse { animation: mcs-pulse 7s ease-in-out infinite; }
        @keyframes mcs-pulse {
          0%, 100% { opacity: 0.55; transform: scale(1); }
          50%      { opacity: 0.85; transform: scale(1.04); }
        }
        .mcs-particle { animation: mcs-float linear infinite; }
        @keyframes mcs-float {
          0%   { transform: translateY(0) translateX(0); opacity: 0; }
          25%  { opacity: 0.35; }
          75%  { opacity: 0.35; }
          100% { transform: translateY(-70px) translateX(8px); opacity: 0; }
        }
      `}</style>
    </footer>
  );
};

export default Footer;
